import { useState, useEffect, useLayoutEffect, useRef } from "react";
import SketchScatter from "./SketchScatter";
import TryMedocx from "./pages/TryMedocx";

/* App.css is not imported on purpose. The landing page carries its own reset
   and type below, so the old upload app's stylesheet stays out of the bundle.
   The file can stay on disk. */

const IOS_URL = "https://apps.apple.com/app/medocx/id6790794013";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=ai.medocx.app";
const CONTACT_EMAIL = "info@medocx.ai";

/* `short` is what a tab shows on phones, where the full label would force
   the bar to wrap or scroll sideways. Change the words freely. */
const ROUTES = [
  { path: "/what-is-medocx", label: "What is Medocx", short: "About", title: "What is Medocx — Medocx" },
  { path: "/try", label: "Try Medocx Here", short: "Try it", title: "Try Medocx — Medocx" },
  { path: "/contact", label: "Contact Us", short: "Contact", title: "Contact — Medocx" },
];

const HOME_TITLE = "Medocx — Scroll Med School";

/* The one route that owns the viewport instead of flowing down the page. */
const FEED_PATH = "/try";

/* ── Tiny router ──────────────────────────────────────────────────────────
   Real URLs via the History API, no dependency. Links stay real <a> tags so
   cmd-click, middle-click and crawlers still work; the handler only
   intercepts a plain left-click. Needs an SPA rewrite on the host so a hard
   refresh on /contact serves index.html instead of a 404. */
function normalize(p) {
  if (!p) return "/";
  const trimmed = p.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function useRouter() {
  const [path, setPath] = useState(() => normalize(window.location.pathname));

  useEffect(() => {
    const onPop = () => setPath(normalize(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const match = ROUTES.find((r) => r.path === path);
    document.title = match ? match.title : HOME_TITLE;
    window.scrollTo(0, 0);
  }, [path]);

  const navigate = (to) => {
    const next = normalize(to);
    if (next === normalize(window.location.pathname)) return;
    window.history.pushState({}, "", next);
    setPath(next);
  };

  return [path, navigate];
}

function Nav({ path, navigate }) {
  const tabsRef = useRef(null);
  const [pill, setPill] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const link = (to) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  };

  /* The solid blue pill slides onto whichever tab is current. It's measured
     from the DOM rather than guessed, so the long/short label swap and font
     loading can't leave it a few pixels off; the observer re-measures
     whenever the tabs change size. On the home page nothing is current, so
     there is no pill and all three tabs show their soft tint. */
  useLayoutEffect(() => {
    const track = tabsRef.current;
    const active = track ? track.querySelector('[aria-current="page"]') : null;
    if (!track || !active) {
      setPill(null);
      return undefined;
    }

    let live = true;
    const measure = () => {
      if (live) setPill({ x: active.offsetLeft, w: active.offsetWidth });
    };
    measure();

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(track);
      ro.observe(active);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

    return () => {
      live = false;
      if (ro) ro.disconnect();
    };
  }, [path]);

  /* Borderless while the page sits at the top, so the bar melts into the
     white page; a hairline and a soft shadow appear once content scrolls
     under it. The feed scrolls inside itself, so there the bar is always
     elevated. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [path]);

  const elevated = scrolled || path === FEED_PATH;

  return (
    <nav className={`landing-nav ${elevated ? "elevated" : ""}`} aria-label="Main">
      <div className="landing-tabs" ref={tabsRef}>
        {pill && (
          <span
            className="landing-tabs-pill"
            aria-hidden="true"
            style={{ transform: `translateX(${pill.x}px)`, width: pill.w }}
          />
        )}
        {ROUTES.map((r) => (
          <a
            key={r.path}
            href={r.path}
            onClick={link(r.path)}
            aria-current={path === r.path ? "page" : undefined}
            className={`landing-tab ${path === r.path ? "active" : ""}`}
          >
            <span className="landing-tab-long">{r.label}</span>
            <span className="landing-tab-short">{r.short}</span>
          </a>
        ))}
      </div>
    </nav>
  );
}

function Home() {
  return (
    <main className="landing-mono-main">
      <SketchScatter />

      <div className="landing-mono-inner">
        <h1 className="landing-mono-headline">
          Scroll Med&nbsp;School.<br />
          <span className="landing-mono-headline-italic">Scroll Medocx.</span>
        </h1>
        {/* One line of context for first-time visitors. Delete if you'd rather
            let the headline stand alone. */}
        <p className="landing-mono-sub">Pre-clinical to Step 1, one swipe at a time.</p>
        <div className="landing-mono-cta">
          <a
            className="landing-mono-btn"
            href={IOS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download for iOS
          </a>
          <a
            className="landing-mono-btn secondary"
            href={ANDROID_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download for Android
          </a>
        </div>
      </div>
    </main>
  );
}

function WhatIsMedocx() {
  return (
    <main className="page">
      <div className="page-inner">
        <h1 className="page-title">
          Doomscrolling,<br />
          <em>pointed at med school.</em>
        </h1>

        <p className="page-lead">
          Medocx is a scrolling app built entirely on med school material, from your first
          pre-clinical block to USMLE Step 1. Same thumb, same ten minutes you were going to lose
          anyway — different feed.
        </p>

        <p>
          You are going to scroll today. Medocx doesn&apos;t ask you to stop, sit down, or block out
          an hour you don&apos;t have. It changes what goes past. High-yield concepts, one swipe at a
          time, in the gaps you were already spending on nothing.
        </p>

        <ul className="formats">
          <li>
            <h2>Short videos</h2>
            <p>A mechanism explained in the time it takes to lose interest in anything else.</p>
          </li>
          <li>
            <h2>Quick questions</h2>
            <p>One stem, one answer, one line on why. Then keep scrolling.</p>
          </li>
          <li>
            <h2>Flashcards</h2>
            <p>Short enough to read standing up, spaced so the misses come back.</p>
          </li>
          <li>
            <h2>Podcast</h2>
            <p>For the commute, the gym, and the walk between buildings.</p>
          </li>
        </ul>

        <p className="page-closer">Studying that feels like the break you were already taking.</p>

        <div className="page-actions">
          <a
            className="landing-mono-btn"
            href={IOS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download for iOS
          </a>
          <a
            className="landing-mono-btn secondary"
            href={ANDROID_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download for Android
          </a>
        </div>
      </div>
    </main>
  );
}

function Contact() {
  return (
    <main className="page page-narrow">
      <div className="page-inner">
        <h1 className="page-title">
          Contact <em>us.</em>
        </h1>
        <p className="page-lead">
          We collaborate with USMLE tutors, prep centers, and creators. Tell us what you have in
          mind.
        </p>
        <a className="contact-email" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>
      </div>
    </main>
  );
}

function LandingPage() {
  const [mounted, setMounted] = useState(false);
  const [path, navigate] = useRouter();

  useEffect(() => { setMounted(true); }, []);

  const isHome = !ROUTES.some((r) => r.path === path);
  const isFeed = path === FEED_PATH;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,500;0,600;1,500;1,600&family=Inter:wght@400;500;600&display=swap');

        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }

        /* The blue is the one your videos already sign off with — the X in the
           MedocX watermark, LOGO_X_COLOR (235,180,70) in BGR = #46B4EB.
           --sky-strong is the same hue taken darker, only so white text on the
           current tab stays readable (#46B4EB behind white text is too faint). */
        .landing-mono {
          --sky: #46B4EB;
          --sky-deep: #2A9BD8;
          --sky-strong: #1A78B2;
          --sky-ink: #145E8C;
          --line: rgba(0,0,0,.08);
          --muted: rgba(0,0,0,.58);
          --gutter: clamp(20px, 5vw, 48px);

          /* Tuning knobs. Other components may read --nav-h, so keep the name.
             Lower --tile-scale or raise --scatter-inset if a sketch still
             touches an edge. */
          --nav-h: 76px;
          --scatter-inset: 16px;
          --tile-scale: .78;

          min-height: 100vh;
          min-height: 100svh;
          width: 100%;
          background: #fff;
          color: #000;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
          line-height: 1.5;
          display: flex;
          flex-direction: column;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          opacity: 0;
          transition: opacity .5s ease;
        }
        .landing-mono.mounted { opacity: 1; }

        /* ── The feed route ─────────────────────────────────────────────────
           /try scrolls INSIDE itself, so the document must not scroll as well
           or the page rubber-bands against the feed and the bar drifts off the
           top. Height, not min-height: the feed takes exactly what's left under
           the bar. 100dvh second so browsers that have it win — 100vh on mobile
           counts the URL bar that isn't there. */
        .landing-mono.is-feed {
          height: 100vh;
          height: 100dvh;
          min-height: 0;
          overflow: hidden;
        }

        /* ── Bar ─────────────────────────────────────────────────────────────
           White glass with the three tabs centred. Borderless while the page
           is at the top; .elevated (set from JS on scroll, always on the feed)
           adds a hairline and a soft shadow so content visibly passes under. */
        .landing-nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          height: var(--nav-h);
          z-index: 20;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 var(--gutter);
          background: rgba(255,255,255,.78);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          backdrop-filter: blur(20px) saturate(180%);
          box-shadow: 0 1px 0 rgba(0,0,0,0), 0 12px 32px -20px rgba(0,0,0,0);
          transition: box-shadow .28s ease, background-color .28s ease;
        }
        .landing-nav.elevated {
          background: rgba(255,255,255,.88);
          box-shadow: 0 1px 0 var(--line), 0 12px 32px -20px rgba(20,94,140,.22);
        }

        /* ── Tabs ────────────────────────────────────────────────────────────
           Three blue pills. Idle, each is a soft tint of --sky with dark-blue
           text; the current one is covered by a solid pill (deep-blue gradient,
           white text) that slides from tab to tab. Its x and width come from
           JS (see Nav). */
        .landing-tabs {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .landing-tabs-pill {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          border-radius: 999px;
          background: linear-gradient(180deg, var(--sky-deep) 0%, var(--sky-strong) 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.28),
            0 1px 2px rgba(26,120,178,.30),
            0 8px 20px -8px rgba(26,120,178,.60);
          pointer-events: none;
          animation: pill-in .22s ease both;
          transition: transform .34s cubic-bezier(.22,.8,.26,1),
                      width .34s cubic-bezier(.22,.8,.26,1);
        }
        @keyframes pill-in { from { opacity: 0; } }
        .landing-tab {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          height: 44px;
          padding: 0 22px;
          border-radius: 999px;
          background: rgba(70,180,235,.12);
          box-shadow: inset 0 0 0 1px rgba(70,180,235,.24);
          font-size: 15px;
          font-weight: 600;
          letter-spacing: -.01em;
          color: var(--sky-ink);
          text-decoration: none;
          white-space: nowrap;
          transition: color .2s ease, background-color .2s ease, box-shadow .2s ease;
        }
        /* The current tab drops its tint so the solid pill underneath shows. */
        .landing-tab.active {
          background: transparent;
          box-shadow: none;
          color: #fff;
        }
        .landing-tab:focus-visible { outline: 2px solid var(--sky-strong); outline-offset: 3px; }
        .landing-tab-short { display: none; }

        /* ── Hero ─────────────────────────────────────────────────────────── */
        .landing-mono-main {
          position: relative;
          flex: 1;
          min-height: 100vh;
          min-height: 100svh;          /* the visible screen, URL bar or not */
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: calc(var(--nav-h) + 24px) var(--gutter) 56px;
          text-align: center;
          overflow: hidden;
        }
        .landing-mono-inner {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 720px;
        }
        /* A soft white halo behind the type so a sketch can sit near the
           headline without fighting it. Invisible against the white page. */
        .landing-mono-inner::before {
          content: "";
          position: absolute;
          inset: -14% -20%;
          z-index: -1;
          border-radius: 50%;
          background: radial-gradient(ellipse at center, rgba(255,255,255,.94) 38%, rgba(255,255,255,0) 72%);
          pointer-events: none;
        }
        .landing-mono-headline {
          font-family: 'Fraunces', Georgia, serif;
          font-size: clamp(40px, 7.6vw, 76px);
          font-weight: 600;
          line-height: 1.02;
          letter-spacing: -.04em;
          color: var(--sky);
        }
        .landing-mono-headline-italic {
          font-style: italic;
          font-weight: 500;
          color: var(--sky-deep);
        }
        .landing-mono-sub {
          margin-top: 20px;
          font-size: clamp(16px, 1.6vw, 19px);
          line-height: 1.5;
          letter-spacing: -.01em;
          color: var(--muted);
          max-width: 36ch;
        }
        .landing-mono-cta {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 34px;
        }

        /* ── Buttons (global; interior pages use them too) ───────────────── */
        .landing-mono-btn {
          appearance: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 48px;
          padding: 0 26px;
          border-radius: 999px;
          border: 1px solid #000;
          background: #000;
          color: #fff;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          font-weight: 500;
          letter-spacing: -.01em;
          line-height: 1;
          text-decoration: none;
          transition: transform .18s ease, border-color .18s ease, background .18s ease;
        }
        .landing-mono-btn:active { transform: translateY(0); }
        .landing-mono-btn:focus-visible { outline: 2px solid var(--sky-deep); outline-offset: 3px; }
        .landing-mono-btn.secondary {
          background: #fff;
          color: #000;
          border-color: rgba(0,0,0,.16);
        }

        /* ── Sketch scatter ─────────────────────────────────────────────────
           Behind the type, never catches a click. Each tile fades in once its
           artwork is traced. The inset pulls every tile in from the edges; the
           canvas scale shrinks artwork without moving it. */
        .scatter {
          position: absolute;
          top: calc(var(--nav-h) + var(--scatter-inset));
          right: var(--scatter-inset);
          bottom: var(--scatter-inset);
          left: var(--scatter-inset);
          z-index: 1;
          pointer-events: none;
        }
        .scatter-item {
          position: absolute;
          opacity: 0;
          transition: opacity .45s ease;
        }
        .scatter-item canvas {
          display: block;
          transform: scale(var(--tile-scale));
          transform-origin: center center;
        }

        /* ── Interior pages (global, so src/pages can use them) ───────────── */
        .page {
          flex: 1;
          padding: calc(var(--nav-h) + clamp(48px, 10vh, 112px)) var(--gutter)
                   clamp(72px, 12vw, 128px);
        }
        .page-inner { max-width: 680px; margin: 0 auto; }
        .page-narrow .page-inner { max-width: 560px; }

        .page-title {
          font-family: 'Fraunces', Georgia, serif;
          font-size: clamp(38px, 6.2vw, 68px);
          font-weight: 600;
          line-height: 1.02;
          letter-spacing: -.04em;
          color: var(--sky);
          text-wrap: balance;
        }
        .page-title em { font-style: italic; font-weight: 500; color: var(--sky-deep); }

        .page-lead {
          margin-top: 26px;
          font-size: clamp(18px, 2vw, 22px);
          line-height: 1.5;
          letter-spacing: -.015em;
          color: #000;
          max-width: 54ch;
        }
        .page p:not(.page-lead):not(.page-closer) {
          margin-top: 20px;
          font-size: 17px;
          line-height: 1.7;
          color: var(--muted);
          max-width: 60ch;
        }

        .formats {
          list-style: none;
          margin-top: 48px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
          gap: 28px 40px;
        }
        .formats li { border-top: 2px solid var(--sky); padding-top: 14px; }
        .formats h2 { font-size: 16px; font-weight: 600; letter-spacing: -.015em; margin-bottom: 6px; }
        .page .formats p { margin-top: 0; font-size: 15px; line-height: 1.6; }

        .page-closer {
          margin-top: 52px;
          font-family: 'Fraunces', Georgia, serif;
          font-style: italic;
          font-weight: 500;
          font-size: clamp(22px, 3vw, 30px);
          line-height: 1.25;
          letter-spacing: -.03em;
          color: var(--sky-deep);
          max-width: 22ch;
          text-wrap: balance;
        }
        .page-actions { margin-top: 40px; display: flex; flex-wrap: wrap; gap: 12px; }

        /* ── Contact ──────────────────────────────────────────────────────── */
        .contact-email {
          display: inline-block;
          margin-top: 36px;
          font-family: 'Fraunces', Georgia, serif;
          font-size: clamp(22px, 3.6vw, 36px);
          font-weight: 600;
          letter-spacing: -.03em;
          color: #000;
          text-decoration: none;
          border-bottom: 2px solid var(--sky);
          padding-bottom: 3px;
          overflow-wrap: anywhere;
          transition: color .18s ease;
        }
        .contact-email:focus-visible { outline: 2px solid var(--sky-deep); outline-offset: 4px; }

        /* ── Footer ───────────────────────────────────────────────────────── */
        .landing-foot {
          border-top: 1px solid var(--line);
          padding: 22px var(--gutter);
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 8px 16px;
          font-size: 13px;
          color: rgba(0,0,0,.45);
        }
        .landing-foot a { color: inherit; text-decoration: none; }

        /* ── Hover only where there's a pointer, so a tap never leaves a
           button stuck in its hover state. ──────────────────────────────── */
        @media (hover: hover) {
          .landing-tab:not(.active):hover {
            background: rgba(70,180,235,.22);
            box-shadow: inset 0 0 0 1px rgba(70,180,235,.40);
            color: var(--sky-strong);
          }
          .landing-mono-btn:hover { transform: translateY(-1px); }
          .landing-mono-btn:not(.secondary):hover { background: #222; border-color: #222; }
          .landing-mono-btn.secondary:hover { border-color: #000; }
          .contact-email:hover { color: var(--sky-deep); }
          .landing-foot a:hover { color: #000; }
        }

        /* ── Phones ─────────────────────────────────────────────────────────
           Short labels, centred. The bar drops to 64px, the sketches shrink
           and fade behind the type, buttons go full width. If the sketches
           still crowd a small screen, hide .scatter in this block with
           display:none and the hero becomes pure type. */
        @media (max-width: 640px) {
          .landing-mono { --nav-h: 64px; --scatter-inset: 6px; --tile-scale: .5; }

          .landing-tabs { gap: 8px; }
          .landing-tab { height: 40px; padding: 0 18px; font-size: 14px; }
          .landing-tab-long { display: none; }
          .landing-tab-short { display: inline; }

          .scatter { opacity: .55; }

          .landing-mono-main { padding: calc(var(--nav-h) + 16px) var(--gutter) 40px; }
          .landing-mono-cta { flex-direction: column; width: 100%; max-width: 320px; margin-top: 30px; }
          .landing-mono-cta .landing-mono-btn { width: 100%; }
          .page-actions .landing-mono-btn { width: 100%; max-width: 320px; }
        }

        /* Very narrow phones: keep all three tabs on one row. */
        @media (max-width: 360px) {
          .landing-tabs { gap: 6px; }
          .landing-tab { padding: 0 13px; font-size: 13.5px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .landing-mono, .scatter-item, .landing-mono-btn, .landing-tab, .landing-tabs-pill, .landing-nav { transition: none; }
          .landing-tabs-pill { animation: none; }
        }
      `}</style>

      <div className={`landing-mono ${mounted ? "mounted" : ""} ${isFeed ? "is-feed" : ""}`}>
        <Nav path={path} navigate={navigate} />

        {isHome && <Home />}
        {path === "/what-is-medocx" && <WhatIsMedocx />}
        {path === "/try" && <TryMedocx />}
        {path === "/contact" && <Contact />}

        {/* No footer under the feed: it owns the viewport, so a footer below it
            would exist only as a second scroll nobody can reach. */}
        {!isHome && !isFeed && (
          <footer className="landing-foot">
            <span>© {new Date().getFullYear()} Medocx</span>
            <span>
              <a href="/privacypolicy">Privacy</a> &nbsp;·&nbsp; <a href="/terms">Terms</a>
            </span>
          </footer>
        )}
      </div>
    </>
  );
}

export default function App() {
  return <LandingPage />;
}
